/**
 * Loading, empty and error, as first-class screens.
 *
 * These exist as named components because the screenshot matrix captures all four states of
 * every signature screen — loaded, loading, empty, error — and a state that only exists as an
 * inline ternary cannot be captured, reviewed or made consistent.
 *
 * The empty state says what would fill it. "No data" tells someone their screen is broken;
 * "No packages have reported yet this period" tells them what to do next.
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export function Skeleton({ rows = 3, label }: { rows?: number; label?: string }) {
  const { t } = useTranslation()
  return (
    <div className="state state--loading" role="status" aria-live="polite">
      <span className="visually-hidden">{label ?? t('state.loading')}</span>
      {Array.from({ length: rows }, (_, index) => (
        <span key={index} className="skeleton" aria-hidden="true" />
      ))}
    </div>
  )
}

export function Empty({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    // Addressable, so the screenshot matrix can wait for an empty state rather than for a
    // timeout. Every screen renders at most one at a time.
    <div className="state state--empty" data-testid="state-empty">
      <p className="state__title">{title}</p>
      {hint ? <p className="state__hint">{hint}</p> : null}
    </div>
  )
}

export function Failed({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="state state--error" role="alert">
      <p className="state__title">{t('state.error')}</p>
      <p className="state__hint">{message}</p>
      {onRetry ? (
        <button type="button" className="button button--quiet" onClick={onRetry}>
          {t('state.retry')}
        </button>
      ) : null}
    </div>
  )
}
